import React from 'react';
import { Check, Users, BookText, GraduationCap } from 'lucide-react';

interface PriceCardProps {
  title: string;
  price: string;
  features: string[];
  color: string;
  shadowColor: string;
  accentColor: string;
  icon: React.ReactNode;
  popular?: boolean;
}

const PriceCard: React.FC<PriceCardProps> = ({ 
  title, 
  price,
  features, 
  color, 
  shadowColor,
  accentColor,
  icon,
  popular = false
}) => {
  return (
    <div className={`relative rounded-2xl ${popular ? 'border-3 ' + accentColor + ' scale-105' : 'border-2 border-gray-200'} bg-white p-8 shadow-xl transition-all duration-300 hover:shadow-2xl transform hover:-translate-y-2`}>
      {popular && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <span className={`${color} text-white text-sm font-bold px-6 py-2 rounded-full flex items-center shadow-lg`}>
            <Users className="h-4 w-4 ml-2" />
            الأكثر شيوعاً
          </span>
        </div>
      )}
      
      <div className="flex items-center mb-6">
        <div className={`${shadowColor} rounded-xl p-4 ml-4`}>
          {icon}
        </div>
        <h3 className="text-2xl font-bold text-gray-800">{title}</h3>
      </div>
      
      {/* السعر الشهري */}
      <div className="mb-8">
        <div className="text-center p-6 bg-gradient-to-r from-red-50 to-pink-50 rounded-xl border-2 border-red-200 relative">
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <span className="bg-red-500 text-white text-sm font-bold px-4 py-1 rounded-full shadow-lg">
              خصم خاص
            </span>
          </div>
          <h4 className="text-lg font-bold text-blue-800 mb-3">الباقة الشهرية</h4>
          <div className="mb-2">
            <span className="text-2xl text-gray-500 line-through">450 ريال</span>
          </div>
          <div className="flex items-end justify-center mb-3">
            <span className="text-4xl font-bold text-blue-900">350</span>
            <span className="mr-2 text-blue-700 text-lg">/شهر</span>
          </div>
          <div className="text-sm text-red-600 font-bold mb-2">
            وفر {title === 'المرحلة الثانوية' ? '150' : '170'} ريال شهرياً!
          </div>
          <div className="text-sm text-blue-700 font-semibold bg-blue-100 px-3 py-1 rounded-full inline-block">
            8 حصص في الشهر
          </div>
        </div>
      </div>

      <ul className="mb-10 space-y-4">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start">
            <Check className={`h-6 w-6 ${color.replace('bg-', 'text-')} ml-3 mt-0.5 flex-shrink-0`} />
            <span className="text-gray-600 text-lg">{feature}</span>
          </li>
        ))}
      </ul>
      
      <a 
        href="https://wa.me/+9660590317360"
        target="_blank"
        rel="noopener noreferrer"
        className={`block w-full py-4 rounded-xl font-bold text-lg ${color} text-white transition-all duration-300 hover:opacity-90 transform hover:scale-105 text-center shadow-lg`}
      >
        اشترك الآن
      </a>
    </div>
  );
};

const Pricing = () => {
  return (
    <section id="pricing" className="py-20 md:py-28 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* العنوان الرئيسي */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            خطط الاشتراك الشهرية
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            اختر الخطة المناسبة لك - باقات شهرية تشمل 8 حصص تفاعلية مباشرة مع أفضل المدرسين
          </p>
        </div>
        
        {/* بطاقات الأسعار */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 max-w-7xl mx-auto mb-16">
          <PriceCard
            title="المرحلة الابتدائية"
            price="250"
            features={[
              "8 حصص تفاعلية شهرياً مع المدرس",
              "تغطية شاملة للمنهج الدراسي",
              "مساعدة وشرح مفصل للواجبات",
              "أوراق عمل تدريبية مخصصة",
              "متابعة مستمرة للتقدم الأكاديمي",
              "مرونة في تحديد مواعيد الحصص"
            ]}
            color="bg-blue-700"
            shadowColor="bg-blue-100"
            accentColor="border-blue-300"
            icon={<span className="text-blue-700 text-2xl font-bold">ا</span>}
          />
          
          <PriceCard
            title="المرحلة المتوسطة"
            price="250"
            features={[
              "8 حصص تفاعلية مكثفة شهرياً",
              "تغطية المواد المتقدمة والمعقدة",
              "تحضير شامل للاختبارات الدورية",
              "دعم متخصص في الواجبات والمهام",
              "اختبارات تجريبية مع تقييم مفصل",
              "خطة دراسية مخصصة للطالب"
            ]}
            color="bg-teal-600"
            shadowColor="bg-teal-100"
            accentColor="border-teal-300"
            icon={<span className="text-teal-600 text-2xl font-bold">م</span>}
            popular={true}
          />
          
          <PriceCard
            title="المرحلة الثانوية"
            price="300"
            features={[
              "8 حصص تفاعلية متخصصة شهرياً",
              "تغطية المواد المتقدمة والمعقدة",
              "تحضير مكثف للاختبارات النهائية",
              "دعم شامل في الواجبات والبحوث",
              "اختبارات تجريبية شاملة ومراجعات",
              "استراتيجيات متقدمة للنجاح"
            ]}
            color="bg-amber-500"
            shadowColor="bg-amber-100"
            accentColor="border-amber-300"
            icon={<span className="text-amber-600 text-2xl font-bold">ث</span>}
          />
        </div>

        {/* قسم المميزات الإضافية */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-10 rounded-2xl shadow-xl max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold mb-4">مميزات جميع الباقات</h3>
            <p className="text-xl opacity-90">نقدم لك أفضل تجربة تعليمية مع كل باقة</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
              <div className="flex items-center mb-4">
                <Users className="h-8 w-8 ml-3" />
                <h4 className="text-lg font-bold">حصص تفاعلية</h4>
              </div>
              <p className="opacity-90">تفاعل مباشر مع المدرس</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
              <div className="flex items-center mb-4">
                <BookText className="h-8 w-8 ml-3" />
                <h4 className="text-lg font-bold">منهج شامل</h4>
              </div>
              <p className="opacity-90">تغطية كاملة للمنهج</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
              <div className="flex items-center mb-4">
                <GraduationCap className="h-8 w-8 ml-3" />
                <h4 className="text-lg font-bold">معلمون مؤهلون</h4>
              </div>
              <p className="opacity-90">خبرة وكفاءة عالية</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
              <div className="flex items-center mb-4">
                <Check className="h-8 w-8 ml-3" />
                <h4 className="text-lg font-bold">متابعة مستمرة</h4>
              </div>
              <p className="opacity-90">تقييم وتطوير دائم</p>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <a 
              href="https://wa.me/+9660590317360"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-white text-green-600 font-bold py-4 px-10 rounded-xl hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-xl text-xl"
            >
              ابدأ رحلتك التعليمية الآن
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;